import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import i18n, { TFunction, changeLanguage } from 'i18next';
import { FC, ReactNode, createContext, useCallback, useEffect } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import { CategoryOption } from 'src/assets/static/view_department/useDepartmentCategories';
import useAuth from 'src/hooks/useAuth';
import useLocalStorage from 'src/hooks/useLocalstorage';

import translationCA from 'src/locales/ca.json';
import translationEN from 'src/locales/en.json';
import translationES from 'src/locales/es.json';
import translationFR from 'src/locales/fr.json';
import { LanguageCategories, languageCodes } from 'src/utils/constants';

const resources = {
  en: {
    translation: translationEN,
  },
  es: {
    translation: translationES,
  },
  fr: {
    translation: translationFR,
  },
  ca: {
    translation: translationCA,
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  keySeparator: false,
  interpolation: {
    escapeValue: false,
  },
});

interface LocaleContextProviderProps {
  children: ReactNode;
}

interface LocaleContextProps {
  selectedLanguage: CategoryOption;
  handleLanguageChange: (event: DropDownListChangeEvent) => void;
  t: TFunction<'translation', undefined>;
}

export const LocaleContext = createContext<LocaleContextProps | null>(null);

const LocaleContextProvider: FC<LocaleContextProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [selectedLanguage, setSelectedLanguage] = useLocalStorage(
    'selectedLanguage',
    LanguageCategories[0]
  );

  const handleLanguageChange = useCallback(
    (event: DropDownListChangeEvent) => {
      const { value } = event;
      setSelectedLanguage(value);
      changeLanguage(languageCodes[value.value.toString() as keyof typeof languageCodes]);
    },
    [setSelectedLanguage]
  );

  useEffect(() => {
    let defaultLanguage = selectedLanguage;
    if (user?.LocaleId) {
      defaultLanguage = LanguageCategories.find(
        (ln) => ln.value === Number(user?.LocaleId)
      ) as CategoryOption;
    }
    setSelectedLanguage(defaultLanguage);
    changeLanguage(languageCodes[defaultLanguage.value.toString() as keyof typeof languageCodes]);
  }, [selectedLanguage, setSelectedLanguage, user?.LocaleId]);

  return (
    <LocaleContext.Provider value={{ selectedLanguage, handleLanguageChange, t }}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleContextProvider;
